// src/pages/MyJourney.tsx
import React, { useEffect, useMemo, useState } from "react";
import {
  TrendingUp,
  Star,
  CheckCircle2,
  Clock,
  BookOpen,
  ClipboardList,
  Flame,
  Lock,
  ChevronRight,
  Calendar,
  MessageSquare,
  ArrowRight,
  Zap,
  Loader2,
  AlertCircle,
} from "lucide-react";
import { Link } from "react-router-dom";
import type { MemberTier } from "../../app/api/authApi";
import {
  useGetJourneyProfileQuery,
  useGetJourneyQuartersQuery,
  useGetActivityQuery,
  type ActivityItem as JourneyActivityItem,
  type QuarterData,
  type ActivityType,
} from "../../app/api/journeyApi";
import {
  QuarterKey,
  useGetMyQuarterQuery,
  type QuarterSummaryItem,
} from "../../app/api/programmeApi";

/* ─────────────────────────────────────────────
   UI HELPERS
───────────────────────────────────────────── */

const Bone: React.FC<{ className?: string }> = ({ className = "" }) => (
  <div className={`animate-pulse rounded-xl bg-gray-100 ${className}`} />
);

const SectionHeading: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <h2 className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-4">
    {children}
  </h2>
);

const fmtDate = (d?: string | null) =>
  d
    ? new Date(d).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : "—";

const prettyEventType = (eventType?: string | null) => {
  if (!eventType) return "—";
  return eventType
    .split("_")
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
    .join(" ");
};

const TIER_LABELS: Record<MemberTier, string> = {
  wider_audience: "Wider Audience",
  inner_circle: "Inner Circle",
  legacy_fellows: "Legacy Fellows",
};

const TIER_ORDER: MemberTier[] = [
  "wider_audience",
  "inner_circle",
  "legacy_fellows",
];

const TIER_META: Record<
  MemberTier,
  { colour: string; bg: string; ring: string; dot: string }
> = {
  wider_audience: {
    colour: "text-green-700",
    bg: "bg-green-50",
    ring: "ring-green-200",
    dot: "bg-green-500",
  },
  inner_circle: {
    colour: "text-blue-700",
    bg: "bg-blue-50",
    ring: "ring-blue-200",
    dot: "bg-blue-500",
  },
  legacy_fellows: {
    colour: "text-purple-700",
    bg: "bg-purple-50",
    ring: "ring-purple-200",
    dot: "bg-purple-500",
  },
};

// ─────────────────────────────────────────────
// ACTIVITY_META
//
// Every value in the ActivityType union must have an entry here
// because the record is typed as Record<ActivityType, ...>.
//
// IMPORTANT: video_progress_saved is an internal mid-watch checkpoint
// that should never appear in the visible timeline — it is filtered out
// in the render loop below. But the entry is required here to satisfy
// TypeScript's exhaustive Record check.
// ─────────────────────────────────────────────
const ACTIVITY_META: Record<
  ActivityType,
  { icon: React.ReactNode; colour: string; bg: string }
> = {
  task_started: {
    icon: <Clock size={13} />,
    colour: "text-gray-700",
    bg: "bg-gray-100",
  },
  task_submitted: {
    icon: <ClipboardList size={13} />,
    colour: "text-amber-700",
    bg: "bg-amber-100",
  },
  task_graded: {
    icon: <CheckCircle2 size={13} />,
    colour: "text-green-700",
    bg: "bg-green-100",
  },
  content_viewed: {
    icon: <BookOpen size={13} />,
    colour: "text-blue-700",
    bg: "bg-blue-100",
  },
  content_downloaded: {
    icon: <BookOpen size={13} />,
    colour: "text-blue-700",
    bg: "bg-blue-100",
  },
  event_attended: {
    icon: <Calendar size={13} />,
    colour: "text-purple-700",
    bg: "bg-purple-100",
  },
  fellows_nominated: {
    icon: <Star size={13} />,
    colour: "text-rose-700",
    bg: "bg-rose-100",
  },
  fellows_selected: {
    icon: <Flame size={13} />,
    colour: "text-purple-700",
    bg: "bg-purple-100",
  },
  tier_upgraded: {
    icon: <TrendingUp size={13} />,
    colour: "text-green-700",
    bg: "bg-green-100",
  },
  discussion_posted: {
    icon: <MessageSquare size={13} />,
    colour: "text-indigo-700",
    bg: "bg-indigo-100",
  },
  discussion_liked: {
    icon: <Star size={13} />,
    colour: "text-pink-700",
    bg: "bg-pink-100",
  },
  // Internal checkpoint — filtered out of the visible timeline below.
  // Entry is required for TypeScript's exhaustive Record<ActivityType, ...>.
  video_progress_saved: {
    icon: <BookOpen size={13} />,
    colour: "text-indigo-700",
    bg: "bg-indigo-100",
  },
};

// Activity types that are purely internal and should never appear
// in the user-facing timeline, regardless of what the API returns.
const HIDDEN_ACTIVITY_TYPES = new Set<ActivityType>(["video_progress_saved"]);

/* ─────────────────────────────────────────────
   COMBINED TYPES
───────────────────────────────────────────── */

type QuarterCardData = QuarterData & {
  theme: string;
  tagline: string;
  status: "completed" | "active" | "upcoming" | "locked";
  eventDate?: string | null;
};

/* ─────────────────────────────────────────────
   SUB COMPONENTS
───────────────────────────────────────────── */

const QuarterCard: React.FC<{
  q: QuarterCardData;
  isSelected: boolean;
  onClick: () => void;
}> = ({ q, isSelected, onClick }) => {
  const locked = q.status === "locked";
  const upcoming = q.status === "upcoming";
  const taskPct =
    q.tasksTotal > 0 ? Math.round((q.tasksDone / q.tasksTotal) * 100) : 0;

  return (
    <button
      onClick={locked ? undefined : onClick}
      disabled={locked}
      className={`relative w-full text-left rounded-2xl border transition-all duration-200 overflow-hidden
        ${locked ? "opacity-50 cursor-not-allowed border-gray-100 bg-gray-50" : ""}
        ${upcoming && !isSelected ? "border-gray-200 bg-white hover:border-gray-300" : ""}
        ${q.status === "completed" && !isSelected ? "border-green-100 bg-white hover:border-green-200" : ""}
        ${q.status === "active" && !isSelected ? "border-amber-100 bg-white hover:border-amber-200" : ""}
        ${isSelected ? "border-green-400 bg-white shadow-md" : "shadow-sm"}
      `}
    >
      <div
        className={`h-1 w-full
          ${q.status === "completed" ? "bg-green-400" : ""}
          ${q.status === "active" ? "bg-amber-400" : ""}
          ${q.status === "upcoming" ? "bg-gray-200" : ""}
          ${q.status === "locked" ? "bg-gray-100" : ""}
        `}
      />

      <div className="p-4">
        <div className="flex items-start justify-between mb-2">
          <div>
            <span
              className={`text-xs font-bold uppercase tracking-widest
                ${q.status === "completed" ? "text-green-600" : ""}
                ${q.status === "active" ? "text-amber-600" : ""}
                ${q.status === "upcoming" ? "text-gray-400" : ""}
                ${q.status === "locked" ? "text-gray-300" : ""}
              `}
            >
              {q.quarter}
            </span>
          </div>
          <div className="shrink-0">
            {q.status === "completed" && (
              <CheckCircle2 size={15} className="text-green-500" />
            )}
            {q.status === "active" && (
              <Zap size={15} className="text-amber-500" />
            )}
            {q.status === "upcoming" && (
              <Clock size={15} className="text-gray-400" />
            )}
            {q.status === "locked" && (
              <Lock size={15} className="text-gray-300" />
            )}
          </div>
        </div>

        <p className="text-xs font-semibold text-gray-800 leading-snug mb-1">
          {q.tagline || "Quarter Overview"}
        </p>
        <p className="text-[11px] text-gray-400 leading-snug mb-3 line-clamp-2">
          {q.theme || "Theme TBD"}
        </p>

        {!locked && !upcoming && (
          <div className="space-y-1">
            <div className="flex justify-between text-[11px] text-gray-400">
              <span>Tasks</span>
              <span
                className={
                  q.tasksDone === q.tasksTotal && q.tasksTotal > 0
                    ? "text-green-600 font-semibold"
                    : ""
                }
              >
                {q.tasksDone}/{q.tasksTotal}
              </span>
            </div>
            <div className="h-1 rounded-full bg-gray-100 overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-500
                  ${taskPct === 100 ? "bg-green-500" : taskPct > 0 ? "bg-amber-400" : "bg-gray-200"}`}
                style={{ width: `${taskPct}%` }}
              />
            </div>
          </div>
        )}

        {upcoming && (
          <p className="text-[11px] text-gray-400">
            {q.eventDate ? `Event: ${fmtDate(q.eventDate)}` : "Begins soon"}
          </p>
        )}
        {locked && <p className="text-[11px] text-gray-300">Not yet unlocked</p>}

        {q.avgScore !== null && (
          <div className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-green-700 bg-green-50 px-2 py-0.5 rounded-full">
            <Star size={9} />
            {q.avgScore.toFixed(1)}/10 avg
          </div>
        )}
      </div>
    </button>
  );
};

const ScoreRing: React.FC<{ score: number; max?: number; size?: number }> = ({
  score,
  max = 10,
  size = 64,
}) => {
  const r = size / 2 - 6;
  const circ = 2 * Math.PI * r;
  const dash = (((score / max) * 100) / 100) * circ;
  const pct = Math.round((score / max) * 100);

  return (
    <div
      className="relative flex items-center justify-center"
      style={{ width: size, height: size }}
    >
      <svg
        width={size}
        height={size}
        className="-rotate-90"
        style={{ position: "absolute" }}
      >
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="#f3f4f6"
          strokeWidth="5"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={pct >= 80 ? "#16a34a" : pct >= 60 ? "#f59e0b" : "#ef4444"}
          strokeWidth="5"
          strokeDasharray={`${dash} ${circ}`}
          strokeLinecap="round"
        />
      </svg>
      <div className="flex flex-col items-center leading-none">
        <span className="text-sm font-bold text-gray-900">{score}</span>
        <span className="text-[9px] text-gray-400 font-medium">/{max}</span>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────────── */

const MyJourney: React.FC = () => {
  const {
    data: profileRes,
    isLoading: profileLoading,
    isError: profileError,
  } = useGetJourneyProfileQuery();

  const {
    data: quartersRes,
    isLoading: quartersLoading,
    isError: quartersError,
  } = useGetJourneyQuartersQuery();

  const { data: myQuarterRes, isLoading: programmeLoading } =
    useGetMyQuarterQuery();

  const profile = profileRes?.data;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const quarterBreakdown = quartersRes?.data ?? [];
  const programmeData = myQuarterRes?.data;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const programmeSummary = programmeData?.quarterSummary ?? [];
  const currentQuarter = programmeData?.currentQuarter;

  const combinedQuarters: QuarterCardData[] = useMemo(() => {
    const quarterMap = new Map<QuarterKey, QuarterData>();
    quarterBreakdown.forEach((q) => quarterMap.set(q.quarter, q));

    const summaryMap = new Map<QuarterKey, QuarterSummaryItem>();
    programmeSummary.forEach((q) => summaryMap.set(q.quarter, q));

    const ordered: QuarterKey[] = ["Q1", "Q2", "Q3", "Q4"];

    return ordered.map((quarter) => {
      const qData = quarterMap.get(quarter) ?? {
        quarter,
        tasksTotal: 0,
        tasksDone: 0,
        tasksGraded: 0,
        avgScore: null,
        contentViewed: 0,
        eventAttended: false,
        eventType: null,
        fellowsNominated: false,
        fellowsStatus: null,
        submissions: [],
      };

      const qSummary = summaryMap.get(quarter);

      return {
        ...qData,
        theme: qSummary?.theme ?? "",
        tagline: qSummary?.tagline ?? "",
        status: (qSummary?.status ?? "locked") as QuarterCardData["status"],
        eventDate: qSummary?.eventDate ?? null,
      };
    });
  }, [quarterBreakdown, programmeSummary]);

  const initialQuarter = useMemo<QuarterKey>(() => {
    const active = combinedQuarters.find((q) => q.status === "active")?.quarter;
    const completed = combinedQuarters.find((q) => q.status === "completed")?.quarter;
    return (currentQuarter ?? active ?? completed ?? "Q1") as QuarterKey;
  }, [combinedQuarters, currentQuarter]);

  const [selectedQuarter, setSelectedQuarter] =
    useState<QuarterKey>(initialQuarter);
  const [activityFilter, setActivityFilter] = useState<QuarterKey | "all">("all");
  const [activityPage, setActivityPage] = useState(1);
  const [activityItems, setActivityItems] = useState<JourneyActivityItem[]>([]);

  useEffect(() => {
    setSelectedQuarter(initialQuarter);
  }, [initialQuarter]);

  useEffect(() => {
    setActivityPage(1);
    setActivityItems([]);
  }, [activityFilter]);

  const {
    data: activityRes,
    isLoading: activityLoading,
    isFetching: activityFetching,
  } = useGetActivityQuery({
    quarter: activityFilter === "all" ? undefined : activityFilter,
    limit: 12,
    page: activityPage,
  });

  useEffect(() => {
    if (!activityRes?.data) return;
    setActivityItems((prev) =>
      activityPage === 1
        ? activityRes.data
        : [
            ...prev,
            ...activityRes.data.filter(
              (item) => !prev.some((p) => p.id === item.id),
            ),
          ],
    );
  }, [activityRes, activityPage]);

  const selected = useMemo(
    () =>
      combinedQuarters.find((q) => q.quarter === selectedQuarter) ??
      combinedQuarters[0],
    [combinedQuarters, selectedQuarter],
  );

  const currentTier = (profile?.currentTier ?? "wider_audience") as MemberTier;
  const tierIndex = TIER_ORDER.indexOf(currentTier);

  const joinedDate = profile?.mentee?.programmeStartQuarter
    ? `Programme start ${profile.mentee.programmeStartQuarter}`
    : "Member record available";

  const activityFilterOptions = useMemo<(QuarterKey | "all")[]>(() => {
    const available = combinedQuarters
      .filter((q) => q.status !== "locked")
      .map((q) => q.quarter);
    return ["all", ...available];
  }, [combinedQuarters]);

  const totalEvents = profile?.totalEventsAttended ?? 0;
  const totalTasksSubmitted = profile?.totalTasksSubmitted ?? 0;
  const totalTasksGraded = profile?.totalTasksGraded ?? 0;
  const totalContentViewed = profile?.totalContentViewed ?? 0;
  const quartersCompleted = profile?.quartersCompleted ?? 0;
  const overallAvg = profile?.overallAvgScore ?? null;

  const isLoading = profileLoading || quartersLoading || programmeLoading;

  if (isLoading) {
    return (
      <div className="space-y-8 pb-12">
        <div className="space-y-2">
          <Bone className="h-3 w-36" />
          <Bone className="h-8 w-48" />
          <Bone className="h-4 w-80" />
        </div>
        <Bone className="h-44 w-full rounded-2xl" />
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[0, 1, 2, 3].map((i) => (
            <Bone key={i} className="h-24" />
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          <div className="lg:col-span-2 space-y-3">
            {[0, 1, 2, 3].map((i) => (
              <Bone key={i} className="h-36" />
            ))}
          </div>
          <div className="lg:col-span-3 space-y-3">
            <Bone className="h-72" />
            <Bone className="h-52" />
          </div>
        </div>
      </div>
    );
  }

  if (profileError || quartersError || !profile) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center gap-3">
        <div className="w-12 h-12 rounded-2xl bg-red-50 flex items-center justify-center">
          <AlertCircle size={20} className="text-red-400" />
        </div>
        <p className="text-sm font-semibold text-gray-700">
          Unable to load your journey data
        </p>
        <p className="text-xs text-gray-400 max-w-xs">
          Check your connection and refresh the page.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-8 pb-12">
      {/* PAGE HEADER */}
      <div>
        <p className="text-xs font-medium uppercase tracking-widest text-green-600 mb-1">
          Personal Growth Record
        </p>
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight">
          My Journey
        </h1>
        <p className="mt-1 text-sm text-gray-500">
          Your complete growth record across the GIMLP programme — tasks,
          scores, content, and tier progress.
        </p>
      </div>

      {/* MEMBER IDENTITY CARD */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-green-800 to-green-950 text-white p-6 shadow-lg">
        <div className="pointer-events-none absolute -right-8 -top-8 h-48 w-48 rounded-full border border-white/10" />
        <div className="pointer-events-none absolute -right-2 -top-2 h-28 w-28 rounded-full border border-white/10" />
        <div className="pointer-events-none absolute right-20 bottom-0 h-20 w-20 rounded-full border border-white/5" />

        <div className="relative z-10 flex flex-col sm:flex-row sm:items-center gap-5">
          <div className="shrink-0 w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center text-xl font-bold text-white backdrop-blur-sm">
            {(profile.mentee?.firstName?.[0] ?? "M").toUpperCase()}
            {(profile.mentee?.lastName?.[0] ?? "P").toUpperCase()}
          </div>

          <div className="flex-1">
            <h2 className="text-lg font-bold leading-snug">
              {profile.mentee?.firstName} {profile.mentee?.lastName}
            </h2>
            <p className="text-white/60 text-xs mt-0.5">
              {[profile.mentee?.region, profile.mentee?.province]
                .filter(Boolean)
                .join(" — ") || "Region not set"}
            </p>
            <p className="text-white/50 text-xs">{joinedDate}</p>

            <div className="mt-3 flex items-center gap-2 flex-wrap">
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-full bg-white/20 text-white backdrop-blur-sm">
                <span className="w-1.5 h-1.5 rounded-full bg-green-300" />
                {TIER_LABELS[currentTier]}
              </span>
              {profile.fellowsEligible && (
                <span className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full bg-amber-400/20 text-amber-300">
                  <Flame size={10} />
                  Fellows eligible
                  {profile.fellowsEligibleQuarter
                    ? ` · ${profile.fellowsEligibleQuarter}`
                    : ""}
                </span>
              )}
            </div>
          </div>

          <div className="shrink-0 text-right">
            {overallAvg !== null ? (
              <>
                <p className="text-3xl font-bold tabular-nums">
                  {overallAvg.toFixed(1)}
                </p>
                <p className="text-white/50 text-xs">Overall avg score</p>
              </>
            ) : (
              <>
                <p className="text-3xl font-bold">—</p>
                <p className="text-white/50 text-xs">No grades yet</p>
              </>
            )}
            <p className="mt-2 text-white/60 text-xs">
              {quartersCompleted} quarter{quartersCompleted !== 1 ? "s" : ""}{" "}
              completed
            </p>
          </div>
        </div>
      </div>

      {/* OVERALL STATS */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          {
            label: "Tasks submitted",
            value: String(totalTasksSubmitted),
            sub: `${totalTasksGraded} graded so far`,
            colour: "text-green-700",
            icon: <ClipboardList size={14} className="text-green-500" />,
          },
          {
            label: "Content consumed",
            value: String(totalContentViewed),
            sub: "Viewed or downloaded",
            colour: "text-blue-700",
            icon: <BookOpen size={14} className="text-blue-500" />,
          },
          {
            label: "Events attended",
            value: String(totalEvents),
            sub: "Live or streamed",
            colour: "text-purple-700",
            icon: <Calendar size={14} className="text-purple-500" />,
          },
          {
            label: "Avg. task score",
            value: overallAvg !== null ? `${overallAvg.toFixed(1)}/10` : "—",
            sub: overallAvg !== null ? "Across graded tasks" : "No grades yet",
            colour: "text-amber-600",
            icon: <Star size={14} className="text-amber-500" />,
          },
        ].map((s) => (
          <div
            key={s.label}
            className="bg-white rounded-xl border border-gray-100 shadow-sm px-4 py-4"
          >
            <div className="flex items-center gap-1.5 mb-2">
              {s.icon}
              <p className="text-xs text-gray-400">{s.label}</p>
            </div>
            <p className={`text-xl font-bold ${s.colour}`}>{s.value}</p>
            <p className="text-[11px] text-gray-400 mt-0.5 leading-tight">
              {s.sub}
            </p>
          </div>
        ))}
      </div>

      {/* TWO COLUMN LAYOUT */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <SectionHeading>Quarter timeline</SectionHeading>
          <div className="space-y-3">
            {combinedQuarters.map((q) => (
              <QuarterCard
                key={q.quarter}
                q={q}
                isSelected={selectedQuarter === q.quarter}
                onClick={() => setSelectedQuarter(q.quarter)}
              />
            ))}
          </div>
        </div>

        <div className="lg:col-span-3 space-y-5">
          <SectionHeading>
            {selected?.quarter} detail — {selected?.tagline || "Quarter Overview"}
          </SectionHeading>

          {selected && (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 space-y-5">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-xs text-gray-400 mb-0.5">
                    {selected.quarter}
                    {selected.tagline ? ` · ${selected.tagline}` : ""}
                  </p>
                  <h3 className="text-sm font-semibold text-gray-900 leading-snug">
                    {selected.theme || "Theme TBD"}
                  </h3>
                </div>
                <span
                  className={`shrink-0 text-[11px] font-bold px-2.5 py-1 rounded-full
                    ${selected.status === "completed" ? "bg-green-100 text-green-700" : ""}
                    ${selected.status === "active" ? "bg-amber-100 text-amber-700" : ""}
                    ${selected.status === "upcoming" ? "bg-gray-100 text-gray-500" : ""}
                    ${selected.status === "locked" ? "bg-gray-100 text-gray-400" : ""}
                  `}
                >
                  {selected.status.charAt(0).toUpperCase() + selected.status.slice(1)}
                </span>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <div className="rounded-xl bg-gray-50 p-3 space-y-1">
                  <p className="text-[11px] text-gray-400 font-medium">
                    Tasks done
                  </p>
                  <p
                    className={`text-lg font-bold ${
                      selected.tasksDone === selected.tasksTotal &&
                      selected.tasksTotal > 0
                        ? "text-green-700"
                        : "text-gray-800"
                    }`}
                  >
                    {selected.tasksDone}/{selected.tasksTotal}
                  </p>
                  <div className="h-1 rounded-full bg-gray-200 overflow-hidden">
                    <div
                      className={`h-full rounded-full ${
                        selected.tasksDone === selected.tasksTotal &&
                        selected.tasksTotal > 0
                          ? "bg-green-500"
                          : "bg-amber-400"
                      }`}
                      style={{
                        width:
                          selected.tasksTotal > 0
                            ? `${(selected.tasksDone / selected.tasksTotal) * 100}%`
                            : "0%",
                      }}
                    />
                  </div>
                </div>

                <div className="rounded-xl bg-gray-50 p-3 flex items-center gap-3">
                  {selected.avgScore !== null ? (
                    <>
                      <ScoreRing score={selected.avgScore} size={48} />
                      <div>
                        <p className="text-[11px] text-gray-400 font-medium">
                          Avg score
                        </p>
                        <p className="text-[11px] text-gray-500">
                          {selected.tasksGraded} graded
                        </p>
                      </div>
                    </>
                  ) : (
                    <div>
                      <p className="text-[11px] text-gray-400 font-medium">
                        Avg score
                      </p>
                      <p className="text-lg font-bold text-gray-300">—</p>
                      <p className="text-[11px] text-gray-400">Not graded yet</p>
                    </div>
                  )}
                </div>

                <div className="rounded-xl bg-gray-50 p-3 space-y-1">
                  <p className="text-[11px] text-gray-400 font-medium">
                    Content opened
                  </p>
                  <p className="text-lg font-bold text-blue-700">
                    {selected.contentViewed}
                  </p>
                  <p className="text-[11px] text-gray-400">library items</p>
                </div>

                <div
                  className={`rounded-xl p-3 space-y-1 col-span-2 sm:col-span-1
                    ${selected.eventAttended ? "bg-purple-50" : "bg-gray-50"}`}
                >
                  <p className="text-[11px] text-gray-400 font-medium">Event</p>
                  {selected.eventAttended ? (
                    <>
                      <p className="text-xs font-semibold text-purple-700 flex items-center gap-1">
                        <CheckCircle2 size={12} /> Attended
                      </p>
                      <p className="text-[11px] text-purple-500">
                        {prettyEventType(selected.eventType)}
                      </p>
                    </>
                  ) : (
                    <p className="text-xs font-semibold text-gray-400 flex items-center gap-1">
                      <Clock size={12} />
                      {selected.status === "upcoming" || selected.status === "locked"
                        ? "Upcoming"
                        : "Not attended"}
                    </p>
                  )}
                </div>

                <div
                  className={`rounded-xl p-3 space-y-1 col-span-2 sm:col-span-2
                    ${selected.fellowsNominated ? "bg-amber-50" : "bg-gray-50"}`}
                >
                  <p className="text-[11px] text-gray-400 font-medium">
                    Legacy Fellows
                  </p>
                  {selected.fellowsNominated ? (
                    <p className="text-xs font-semibold text-amber-700 flex items-center gap-1">
                      <Star size={12} /> {selected.fellowsStatus || "Nominated"}
                    </p>
                  ) : (
                    <p className="text-xs text-gray-400">
                      {selected.status === "completed"
                        ? "Not nominated"
                        : "Selection pending"}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {selected && selected.tasksGraded > 0 && selected.submissions.length > 0 && (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
              <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-4">
                Task scores — {selected.quarter}
              </p>
              <div className="space-y-3">
                {selected.submissions
                  .filter((s) => s.score !== null)
                  .sort((a, b) => (a.taskNumber ?? 0) - (b.taskNumber ?? 0))
                  .map((t) => (
                    <div
                      key={t.id}
                      className="flex items-center gap-4 p-3 rounded-xl bg-gray-50"
                    >
                      <ScoreRing score={t.score ?? 0} size={48} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-800 leading-snug">
                          Task {t.taskNumber} — {t.taskTitle}
                        </p>
                        <div className="flex items-center gap-1.5 mt-1 text-[11px] text-gray-400">
                          <MessageSquare size={10} />
                          <span>{t.gradedAt ? fmtDate(t.gradedAt) : "Reviewed"}</span>
                        </div>
                        {t.feedback && (
                          <p className="text-[11px] text-gray-500 mt-1 line-clamp-2">
                            {t.feedback}
                          </p>
                        )}
                      </div>
                      <CheckCircle2
                        size={16}
                        className="shrink-0 text-green-500"
                      />
                    </div>
                  ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* TIER PROGRESSION */}
      <div>
        <SectionHeading>Tier progression</SectionHeading>
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-6">
          <div className="flex items-center gap-0">
            {TIER_ORDER.map((tier, idx) => {
              const isCurrentTier = tier === currentTier;
              const isPast = TIER_ORDER.indexOf(tier) < tierIndex;
              const isFuture = TIER_ORDER.indexOf(tier) > tierIndex;
              const m = TIER_META[tier];

              return (
                <React.Fragment key={tier}>
                  <div className="flex flex-col items-center gap-1.5 flex-1">
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all
                        ${isCurrentTier ? `${m.bg} ${m.ring} border-current ring-2` : ""}
                        ${isPast ? "bg-green-100 border-green-300" : ""}
                        ${isFuture ? "bg-gray-50 border-gray-200" : ""}
                      `}
                    >
                      {isPast && (
                        <CheckCircle2 size={18} className="text-green-500" />
                      )}
                      {isCurrentTier && (
                        <TrendingUp size={16} className={m.colour} />
                      )}
                      {isFuture && <Lock size={14} className="text-gray-300" />}
                    </div>
                    <p
                      className={`text-[10px] font-semibold text-center leading-snug max-w-[72px]
                        ${
                          isCurrentTier
                            ? m.colour
                            : isPast
                              ? "text-green-600"
                              : "text-gray-400"
                        }`}
                    >
                      {TIER_LABELS[tier]}
                    </p>
                    {isCurrentTier && (
                      <span
                        className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${m.bg} ${m.colour}`}
                      >
                        Current
                      </span>
                    )}
                  </div>
                  {idx < TIER_ORDER.length - 1 && (
                    <div
                      className={`flex-1 h-0.5 mb-5 ${
                        idx < tierIndex ? "bg-green-400" : "bg-gray-200"
                      }`}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              {
                label: "Current tier",
                value: TIER_LABELS[currentTier],
                sub: "Your current placement",
              },
              {
                label: "Quarters completed",
                value: String(quartersCompleted),
                sub: "Finished successfully",
              },
              {
                label: "Tasks submitted",
                value: String(totalTasksSubmitted),
                sub: `${totalTasksGraded} graded`,
              },
              {
                label: "Overall score",
                value: overallAvg !== null ? `${overallAvg.toFixed(1)}/10` : "—",
                sub: overallAvg !== null ? "Performance average" : "Awaiting grades",
              },
            ].map((item) => (
              <div key={item.label} className="rounded-xl bg-gray-50 p-3">
                <p className="text-[11px] text-gray-400 font-medium">
                  {item.label}
                </p>
                <p className="text-sm font-bold text-gray-900 mt-1">
                  {item.value}
                </p>
                <p className="text-[11px] text-gray-400 mt-0.5">{item.sub}</p>
              </div>
            ))}
          </div>

          {profile.fellowsEligible && (
            <div className="flex items-center gap-3 rounded-xl bg-amber-50 border border-amber-100 px-4 py-3">
              <Flame size={16} className="text-amber-500 shrink-0" />
              <p className="text-xs text-amber-800 flex-1 leading-relaxed">
                <strong>
                  You may be nominated for Legacy Fellows
                  {profile.fellowsEligibleQuarter
                    ? ` in ${profile.fellowsEligibleQuarter}`
                    : ""}.
                </strong>{" "}
                Keep completing tasks, attending events, and engaging with
                library content to strengthen your candidacy.
              </p>
              <Link
                to="/user/fellows"
                className="shrink-0 flex items-center gap-1 text-xs font-semibold text-amber-700 hover:text-amber-900 transition-colors"
              >
                Learn more <ChevronRight size={12} />
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* ACTIVITY LOG */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <SectionHeading>Activity log</SectionHeading>
          <div className="flex items-center gap-1 flex-wrap">
            {activityFilterOptions.map((f) => (
              <button
                key={f}
                onClick={() => setActivityFilter(f)}
                className={`text-[11px] font-semibold px-2.5 py-1 rounded-lg transition-colors
                  ${
                    activityFilter === f
                      ? "bg-green-600 text-white"
                      : "bg-gray-100 text-gray-500 hover:text-gray-700"
                  }`}
              >
                {f === "all" ? "All" : f}
              </button>
            ))}
          </div>
        </div>

        <div className="relative">
          <div className="absolute left-[18px] top-0 bottom-0 w-px bg-gray-100" />

          <div className="space-y-1 pl-10">
            {activityLoading && activityItems.length === 0 ? (
              <div className="space-y-3">
                {[0, 1, 2, 3].map((i) => (
                  <Bone key={i} className="h-20" />
                ))}
              </div>
            ) : activityItems.length === 0 ? (
              <div className="text-sm text-gray-400 py-8">
                No activity found for this filter.
              </div>
            ) : (
              activityItems
                // ── Filter out internal activity types that are not
                //    meant to appear in the user-facing timeline.
                .filter((item) => !HIDDEN_ACTIVITY_TYPES.has(item.activityType))
                .map((item, idx, visibleItems) => {
                  // Safe lookup — fall back to a neutral style if the type
                  // is somehow not in ACTIVITY_META (e.g. future new types
                  // added before a frontend deploy).
                  const meta = ACTIVITY_META[item.activityType] ?? {
                    icon: <Clock size={13} />,
                    colour: "text-gray-600",
                    bg: "bg-gray-100",
                  };

                  const currentDate = fmtDate(item.createdAt);
                  const prevDate =
                    idx > 0 ? fmtDate(visibleItems[idx - 1].createdAt) : null;
                  const showDateDivider = idx === 0 || prevDate !== currentDate;

                  return (
                    <React.Fragment key={item.id}>
                      {showDateDivider && (
                        <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-400 pt-3 pb-1 pl-0 -ml-10 pl-10">
                          {currentDate}
                        </p>
                      )}
                      <div className="relative flex items-start gap-3 py-2">
                        <div
                          className={`absolute -left-[29px] top-3 w-5 h-5 rounded-full flex items-center justify-center shrink-0 ${meta.bg}`}
                        >
                          <span className={meta.colour}>{meta.icon}</span>
                        </div>

                        <div className="flex-1 bg-white rounded-xl border border-gray-100 px-4 py-3 shadow-sm hover:border-gray-200 transition-colors">
                          <div className="flex items-start justify-between gap-2">
                            <p className="text-sm font-medium text-gray-800 leading-snug">
                              {item.title}
                            </p>
                            {item.score !== null && item.score !== undefined && (
                              <span className="shrink-0 inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full bg-green-100 text-green-700">
                                <Star size={9} /> {item.score}/10
                              </span>
                            )}
                          </div>
                          {item.detail && (
                            <p className="text-[11px] text-gray-400 mt-0.5">
                              {item.detail}
                            </p>
                          )}
                        </div>
                      </div>
                    </React.Fragment>
                  );
                })
            )}
          </div>
        </div>

        {activityFetching && activityItems.length > 0 && (
          <div className="mt-4 flex items-center justify-center gap-2 text-xs text-gray-400">
            <Loader2 size={12} className="animate-spin" />
            Loading more activity…
          </div>
        )}

        {activityRes && activityRes.page < activityRes.pages && (
          <button
            onClick={() => setActivityPage((p) => p + 1)}
            disabled={activityFetching}
            className="mt-4 w-full text-center text-xs font-semibold text-green-600 hover:text-green-700 flex items-center justify-center gap-1.5 py-2 disabled:opacity-50"
          >
            {activityFetching ? (
              <>
                <Loader2 size={12} className="animate-spin" />
                Loading…
              </>
            ) : (
              <>
                Load more activity <ArrowRight size={12} />
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
};

export default MyJourney;